import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import { HashLink } from "react-router-hash-link";
import {
  ToolsIcon,
  LightbulbIcon,
  RocketIcon,
  LockIcon,
  UsersIcon,
  CheckCircleIcon,
  DatabaseIcon,
  CloudIcon,
  CodeIcon,
  DesktopIcon,
  MobileAltIcon,
} from "./IconComponents";
import SEO from "./SEO";
import RelatedServices from "./RelatedServices";

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 2rem 0;
  background-color: var(--light-background);
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: var(--text-color);
`;

const HeroSection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto 2rem;
  line-height: 1.6;
`;

const ContentSection = styled.section`
  padding: 4rem 0;
`;

const SectionTitleQuestion = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 0rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const Card = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CardDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
`;

const ProcessSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ProcessCard = styled.div`
  text-align: center;
  padding: 2rem;
`;

const ProcessNumber = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
`;

const ProcessTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ProcessDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const BenefitsSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--card-background);
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
`;

const BenefitIcon = styled.div`
  color: var(--primary-color);
  font-size: 1.5rem;
  margin-top: 0.2rem;
  flex-shrink: 0;
`;

const BenefitText = styled.div`
  h5 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: var(--text-color);
  }

  p {
    color: var(--text-color);
    line-height: 1.5;
    font-size: 0.95rem;
  }
`;

const TechnologySection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const TechnologyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const TechCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  border-radius: 8px;
  background: var(--light-background);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const TechIcon = styled.div`
  color: var(--primary-color);
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const TechName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const TechDescription = styled.p`
  color: var(--text-color);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const CaseStudySection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const CaseStudyCard = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--primary-color),
      var(--secondary-color)
    );
  }
`;

const CaseStudyTitle = styled.h4`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CaseStudyText = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const CaseStudyResult = styled.div`
  background: var(--light-background);
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid var(--primary-color);

  h5 {
    color: var(--primary-color);
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }

  p {
    color: var(--text-color);
    font-size: 0.9rem;
    margin: 0;
  }
`;

const FAQSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: var(--card-background);
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FAQQuestion = styled.h4`
  padding: 1.5rem;
  margin: 0;
  background: var(--primary-color);
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--secondary-color);
  }
`;

const FAQAnswer = styled.p`
  padding: 1.5rem;
  margin: 0;
  color: var(--text-color);
  line-height: 1.6;
`;

const CTASection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 4rem 0;
  text-align: center;
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CTAContainer = styled.div`
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  padding: 3rem;
  background-color: var(--card-background);
  border-radius: 8px;
`;

const CTAButton = styled(HashLink)`
  display: block;
  width: 50%;
  margin: 2rem auto 0;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 4px;
  background-color: var(--primary-color);
  color: var(--light-text-color);
  font-size: 1.05rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DevOps: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const advantages = [
    {
      icon: <RocketIcon />,
      title: "Schnellere Releases",
      description:
        "Durch automatisierte CI/CD-Pipelines bringen wir Ihre Software schneller, häufiger und zuverlässiger in Produktion.",
    },
    {
      icon: <LockIcon />,
      title: "Stabile & sichere Infrastruktur",
      description:
        "Infrastructure as Code, automatisierte Sicherheits-Checks und konsistente Umgebungen reduzieren Ausfälle und Sicherheitsrisiken.",
    },
    {
      icon: <LightbulbIcon />,
      title: "Volle Transparenz",
      description:
        "Mit Monitoring, Logging und Alerting behalten Sie jederzeit den Überblick über den Zustand Ihrer Systeme.",
    },
    {
      icon: <ToolsIcon />,
      title: "Skalierbare Architektur",
      description:
        "Container und Cloud-native Technologien ermöglichen es, Ihre Anwendungen flexibel an wachsende Anforderungen anzupassen.",
    },
  ];

  const process = [
    {
      title: "Analyse & Bestandsaufnahme",
      description:
        "Wir analysieren Ihre bestehende Infrastruktur, Entwicklungsprozesse und Deployment-Abläufe und identifizieren Optimierungspotenziale.",
    },
    {
      title: "CI/CD-Konzeption",
      description:
        "Gemeinsam entwickeln wir eine passende Automatisierungsstrategie für Build, Test und Deployment Ihrer Anwendungen.",
    },
    {
      title: "Implementierung & Automatisierung",
      description:
        "Aufbau von Pipelines, Infrastructure as Code, Containerisierung und automatisierten Tests für einen reibungslosen Softwarelebenszyklus.",
    },
    {
      title: "Monitoring & Weiterentwicklung",
      description:
        "Einrichtung von Monitoring- und Alerting-Systemen sowie kontinuierliche Optimierung der Prozesse und Infrastruktur.",
    },
  ];

  const benefits = [
    {
      title: "Kürzere Time-to-Market",
      description:
        "Automatisierte Pipelines verkürzen die Zeit von der Code-Änderung bis zur Produktivsetzung erheblich.",
    },
    {
      title: "Weniger manuelle Fehler",
      description:
        "Automatisierte Prozesse ersetzen fehleranfällige manuelle Schritte bei Deployment und Konfiguration.",
    },
    {
      title: "Höhere Verfügbarkeit",
      description:
        "Durch Monitoring, automatisiertes Skalieren und schnelle Rollbacks steigt die Ausfallsicherheit Ihrer Systeme.",
    },
    {
      title: "Reproduzierbare Umgebungen",
      description:
        "Infrastructure as Code sorgt für konsistente Entwicklungs-, Test- und Produktionsumgebungen.",
    },
    {
      title: "Effiziente Zusammenarbeit",
      description:
        "DevOps-Praktiken fördern die Zusammenarbeit zwischen Entwicklung und Betrieb und beschleunigen Entscheidungswege.",
    },
    {
      title: "Kosteneffizienz",
      description:
        "Automatisierung und optimierte Cloud-Ressourcennutzung senken langfristig Betriebskosten.",
    },
  ];

  const technologies = [
    {
      icon: <CodeIcon />,
      name: "CI/CD-Pipelines",
      description: "GitHub Actions, Azure DevOps, GitLab CI, Jenkins",
    },
    {
      icon: <DatabaseIcon />,
      name: "Infrastructure as Code",
      description: "Terraform, ArgoCD, Ansible, Bicep, ARM-Templates",
    },
    {
      icon: <CloudIcon />,
      name: "Cloud-Plattformen",
      description: "Microsoft Azure, AWS, Google Cloud",
    },
    {
      icon: <MobileAltIcon />,
      name: "Containerisierung",
      description: "Docker, Kubernetes, Azure Container Apps",
    },
    {
      icon: <ToolsIcon />,
      name: "Monitoring & Logging",
      description: "Azure Monitor, Grafana, Prometheus, ELK-Stack",
    },
    {
      icon: <DesktopIcon />,
      name: "Versionsverwaltung",
      description: "Git, GitHub, GitLab, Azure Repos",
    },
  ];

  const caseStudies = [
    {
      title: "Automatisierte Deployment-Pipeline für SaaS-Anbieter",
      description:
        "Ein Softwareunternehmen deployte manuell und fehleranfällig auf Produktionsserver. Wir implementierten eine vollständige CI/CD-Pipeline mit automatisierten Tests und Rollback-Mechanismen.",
      result: {
        title: "Ergebnisse:",
        description:
          "Deployments von mehreren Stunden auf wenige Minuten reduziert, 90% weniger produktionsrelevante Fehler, mehrfach tägliche Releases möglich",
      },
    },
    {
      title: "Cloud-Infrastruktur als Code für mittelständisches Unternehmen",
      description:
        "Die IT-Infrastruktur war manuell konfiguriert und schwer nachvollziehbar. Wir überführten die gesamte Umgebung in versionierten Infrastructure-as-Code und automatisierten das Provisioning.",
      result: {
        title: "Ergebnisse:",
        description:
          "Neue Umgebungen in Minuten statt Tagen aufgesetzt, vollständige Nachvollziehbarkeit aller Änderungen, deutlich reduzierte Konfigurationsfehler",
      },
    },
    {
      title: "Containerisierung & Monitoring für wachsende Plattform",
      description:
        "Eine stark wachsende Anwendung stieß an die Grenzen der bestehenden Serverarchitektur. Wir containerisierten die Anwendung, führten Kubernetes ein und etablierten umfassendes Monitoring.",
      result: {
        title: "Ergebnisse:",
        description:
          "Automatische Skalierung bei Lastspitzen, 99,9% Verfügbarkeit, frühzeitige Erkennung von Problemen durch proaktives Monitoring",
      },
    },
  ];

  const faqs = [
    {
      question: "Was genau versteht man unter DevOps?",
      answer:
        "DevOps bezeichnet die Verzahnung von Softwareentwicklung (Development) und IT-Betrieb (Operations) durch automatisierte Prozesse, gemeinsame Verantwortung und kontinuierliche Verbesserung. Ziel ist es, Software schneller, stabiler und sicherer bereitzustellen.",
    },
    {
      question: "Für welche Unternehmen lohnt sich DevOps?",
      answer:
        "DevOps-Praktiken lohnen sich für nahezu jedes Unternehmen, das eigene Software entwickelt oder betreibt – von Startups bis zu etablierten mittelständischen Unternehmen. Besonders profitieren Teams mit häufigen Releases oder wachsender Infrastruktur.",
    },
    {
      question: "Wie lange dauert die Einführung von DevOps-Prozessen?",
      answer:
        "Erste sichtbare Verbesserungen, etwa durch eine automatisierte Pipeline, lassen sich oft schon nach wenigen Wochen erzielen. Eine umfassende DevOps-Transformation inklusive Infrastructure as Code und Monitoring dauert je nach Umfang 2-6 Monate.",
    },
    {
      question: "Müssen wir unsere bestehende Infrastruktur komplett ersetzen?",
      answer:
        "Nein. Wir gehen in der Regel schrittweise vor und integrieren DevOps-Praktiken in Ihre bestehende Umgebung. Bestehende Systeme werden schrittweise automatisiert, containerisiert oder in die Cloud migriert – ganz nach Ihren Anforderungen.",
    },
    {
      question: "Was kostet die Einführung von DevOps-Praktiken?",
      answer:
        "Die Kosten hängen stark vom Umfang und der aktuellen Ausgangssituation ab. Eine Analyse und erste Automatisierung beginnt ab 1.500€, umfassendere Projekte inklusive Infrastructure as Code und Monitoring ab 5.000€. Gerne erstellen wir Ihnen ein individuelles Angebot.",
    },
  ];

  return (
    <PageContainer>
      <SEO
        title="DevOps: CI/CD, Automatisierung & Cloud-Infrastruktur"
        description="Wir automatisieren Ihre Softwarebereitstellung mit CI/CD-Pipelines, Infrastructure as Code, Containerisierung und Monitoring – für schnellere Releases und stabile Systeme."
        path="/devops"
        keywords="DevOps, CI/CD, Infrastructure as Code, Kubernetes, Docker, Cloud-Automatisierung, Monitoring"
      />
      <Container>
        <SectionTitle>
          <Trans>
            DevOps <span>Experten</span>
          </Trans>
        </SectionTitle>
        <SectionSubtitle>
          Wir automatisieren Ihre Softwareentwicklung und -bereitstellung –
          von CI/CD-Pipelines über Infrastructure as Code bis hin zu
          Containerisierung und Monitoring – für schnellere, stabilere und
          sicherere Releases.
        </SectionSubtitle>
      </Container>

      <ContentSection>
        <Container>
          <SectionTitleQuestion>
            Warum <span>DevOps</span>?
          </SectionTitleQuestion>
          <Grid>
            {advantages.map((advantage, index) => (
              <Card key={index}>
                <IconWrapper>{advantage.icon}</IconWrapper>
                <CardTitle>{advantage.title}</CardTitle>
                <CardDescription>{advantage.description}</CardDescription>
              </Card>
            ))}
          </Grid>
        </Container>
      </ContentSection>

      <ProcessSection>
        <Container>
          <SectionTitle>
            Unser <span>DevOps-Prozess</span>
          </SectionTitle>
          <ProcessGrid>
            {process.map((step, index) => (
              <ProcessCard key={index}>
                <ProcessNumber>{index + 1}</ProcessNumber>
                <ProcessTitle>{step.title}</ProcessTitle>
                <ProcessDescription>{step.description}</ProcessDescription>
              </ProcessCard>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <BenefitsSection>
        <Container>
          <SectionTitle>
            Ihre <span>Vorteile</span>
          </SectionTitle>
          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>
                  <CheckCircleIcon />
                </BenefitIcon>
                <BenefitText>
                  <h5>{benefit.title}</h5>
                  <p>{benefit.description}</p>
                </BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>
        </Container>
      </BenefitsSection>

      <TechnologySection>
        <Container>
          <SectionTitle>
            Unsere <span>Tools & Technologien</span>
          </SectionTitle>
          <TechnologyGrid>
            {technologies.map((tech, index) => (
              <TechCard key={index}>
                <TechIcon>{tech.icon}</TechIcon>
                <TechName>{tech.name}</TechName>
                <TechDescription>{tech.description}</TechDescription>
              </TechCard>
            ))}
          </TechnologyGrid>
        </Container>
      </TechnologySection>

      <CaseStudySection>
        <Container>
          <SectionTitle>
            Erfolgreiche <span>DevOps-Projekte</span>
          </SectionTitle>
          <CaseStudyGrid>
            {caseStudies.map((caseStudy, index) => (
              <CaseStudyCard key={index}>
                <CaseStudyTitle>{caseStudy.title}</CaseStudyTitle>
                <CaseStudyText>{caseStudy.description}</CaseStudyText>
                <CaseStudyResult>
                  <h5>{caseStudy.result.title}</h5>
                  <p>{caseStudy.result.description}</p>
                </CaseStudyResult>
              </CaseStudyCard>
            ))}
          </CaseStudyGrid>
        </Container>
      </CaseStudySection>

      <FAQSection>
        <Container>
          <SectionTitle>
            Häufig gestellte <span>Fragen</span>
          </SectionTitle>
          <FAQContainer>
            {faqs.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion>{faq.question}</FAQQuestion>
                <FAQAnswer>{faq.answer}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </Container>
      </FAQSection>

      <Container>
        <CTAContainer>
          <SectionTitleQuestion>
            <Trans>
              Bereit für <span>automatisierte Prozesse?</span>
            </Trans>
          </SectionTitleQuestion>
          <SectionSubtitle>
            Lassen Sie uns gemeinsam Ihre Softwarebereitstellung
            automatisieren und Ihre Infrastruktur zukunftssicher machen.
            Kontaktieren Sie uns für eine kostenlose Beratung.
            <CTAButton smooth to="/#contact">
              Kostenloses Erstgespräch
            </CTAButton>
          </SectionSubtitle>
        </CTAContainer>
      </Container>

      <RelatedServices currentPath="/devops" />
    </PageContainer>
  );
};

export default DevOps;
